import React, { useState } from 'react';
import {
  RotateCcw,
  SkipBack,
  Play,
  Pause,
  SkipForward,
  RotateCw,
  Square,
  ChevronUp,
  ChevronDown,
} from 'react-feather';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import CircularProgress from '@mui/material/CircularProgress';
import { AudioPlayerTrackProgress } from '@trinity/chat-components/components/AudioPlayer/Progress.Track';

interface AudioControlsProps {
  isPlaying: boolean;
  isLoading?: boolean;
  onTogglePlay: () => void;
  onPreviousTrack: () => void;
  onNextTrack: () => void;
  onSkipBackward: () => void;
  onSkipAhead: () => void;
  onStop: () => void;
  currentTime?: number;
  duration?: number;
  onScan: (time: number) => void;
  onSeek?: (time: number) => void;
  disabledPreviousTrack?: boolean;
  disabledNextTrack?: boolean;
}

const ControlsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
});

const Button = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  width: 26,
  height: 26,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  '&:hover': {
    backgroundColor: theme.isTrinityThemed ? theme.palette.gray.light5 : theme.palette.btnPrimary.bg,
  },
  '&:disabled': {
    svg: {
      fill: theme.palette.gray.light1,
      stroke: theme.palette.gray.light1,
    },
  },
  '&:disabled:hover': {
    cursor: 'default',
    background: 'none',
  },
  transition: 'background-color 110ms ease-in-out',
}));

const RotateButton = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  width: 26,
  height: 26,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  '&:hover': {
    backgroundColor: theme.isTrinityThemed ? theme.palette.gray.light5 : theme.palette.btnPrimary.bg,
  },
  '&:disabled': {
    svg: {
      stroke: theme.palette.gray.light1,
    },
  },
  '&:disabled:hover': {
    background: 'none',
    cursor: 'default',
    svg: {
      stroke: theme.palette.gray.light1,
    },
  },
  transition: 'background-color 110ms ease-in-out',
}));

const Filled = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  svg: {
    fill: theme.palette.gray.dark1,
    stroke: theme.palette.gray.dark1,
  },
}));

const TimeDisplay = styled('span')(({ theme }) => ({
  fontSize: '0.8rem',
  minWidth: '50px',
  whiteSpace: 'nowrap',
  color: theme.palette.gray.dark1,
}));

const ProgressAndTimer = styled('div')({
  display: 'flex',
  marginTop: '8px',
  justifyContent: 'center',
  minWidth: '110px',
});

const ProgressAndTimerCompressed = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  minWidth: '110px',
  marginLeft: '8px',
});

const StyledProgressTrack = styled(AudioPlayerTrackProgress)({
  width: 100,
  marginRight: '12px',
  maxWidth: 100,
  position: 'relative',
});

const StyledChevronUp = styled(ChevronUp)(({ theme }) => ({
  color: theme.palette.gray.dark1,
  cursor: 'pointer',
  transition: `transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms`,
}));

const StyledChevronDown = styled(ChevronDown)(({ theme }) => ({
  color: theme.palette.gray.dark1,
  cursor: 'pointer',
  transition: `transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms`,
}));

function formatTime(seconds?: number) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? '0' + s : s}`;
}

// TODO: implement disabled state (styling) for buttons

const AudioControls: React.FC<AudioControlsProps> = ({
  isPlaying,
  isLoading,
  onTogglePlay,
  onPreviousTrack,
  onNextTrack,
  onSkipBackward,
  onSkipAhead,
  onStop,
  currentTime,
  duration,
  onScan,
  onSeek,
  disabledPreviousTrack,
  disabledNextTrack,
}) => {
  const theme = useTheme();

  const [isExpanded, setIsExpanded] = useState(true);

  const switchExpandedState = () => {
    setIsExpanded(!isExpanded);
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress size={18} thickness={5} sx={{ color: theme.palette.gray.dark1 }} />
        <LoadingLabel>Loading audio...</LoadingLabel>
        <Button onClick={onStop}>
          <Filled>
            <Square size={14} />
          </Filled>
        </Button>
      </LoadingContainer>
    );
  }

  return (
    <div>
      {isExpanded ? (
        <>
          <ControlsContainer>
            <RotateButton onClick={onSkipBackward} disabled={currentTime === 0}>
              <RotateCcw size={14} color={theme.palette.gray.dark1} />
            </RotateButton>

            <Button onClick={onPreviousTrack} disabled={disabledPreviousTrack}>
              <Filled>
                <SkipBack size={14} />
              </Filled>
            </Button>

            <Button
              style={{ width: 34, height: 34 }}
              onClick={onTogglePlay}>
              <Filled>
                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
              </Filled>
            </Button>

            <Button onClick={onNextTrack} disabled={disabledNextTrack}>
              <Filled>
                <SkipForward size={14} />
              </Filled>
            </Button>

            <RotateButton onClick={onSkipAhead} disabled={currentTime === duration}>
              <RotateCw size={14} color={theme.palette.gray.dark1} />
            </RotateButton>

            <Button onClick={onStop}>
              <Filled>
                <Square size={14} />
              </Filled>
            </Button>

            <Button onClick={switchExpandedState}>
              <StyledChevronUp />
            </Button>

          </ControlsContainer>
          <ProgressAndTimer>
            <StyledProgressTrack
              position={currentTime}
              scan={onScan}
              seek={onSeek}
            />
            <TimeDisplay>
              {formatTime(currentTime)} / {formatTime(duration)}
            </TimeDisplay>
          </ProgressAndTimer>
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <ProgressAndTimerCompressed>
            <StyledProgressTrack
              position={currentTime}
              scan={onScan}
              seek={onSeek}
            />

          </ProgressAndTimerCompressed>
          <ControlsContainer>
            <Button
              style={{ width: 34, height: 34 }}
              onClick={onTogglePlay}>
              <Filled>
                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
              </Filled>
            </Button>

            <Button onClick={switchExpandedState}>
              <StyledChevronDown />
            </Button>
          </ControlsContainer>
        </div>
      )}

    </div>
  );
};

const LoadingContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  padding: '4px 0',
});

const LoadingLabel = styled('span')(({ theme }) => ({
  fontSize: '0.8rem',
  color: theme.palette.gray.dark1,
  whiteSpace: 'nowrap',
}));

export { AudioControls }
export default AudioControls;
